import React from 'react';
import { Box, Container, Card, CardContent, Typography, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SideNavigationBar from './SideNavigationBar';
import CompanyProfile from '../Dashboard/CompanyProfile';

const LayoutContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#07090e',
  position: 'relative',
  paddingLeft: '284px', // Space for Sidebar (24px margin + 260px width) when sidebar is card
  overflowX: 'hidden',
}));

// HeaderContainer removed, Sidebar takes over navigation.
// Mobile view header can be added later if responsive design becomes a priority.

const ContentContainer = styled(Container)({
  paddingTop: '32px',
  paddingBottom: '40px',
  paddingLeft: '40px',
  paddingRight: '40px',
  minHeight: '100vh',
  maxWidth: '1280px',
});

const BackgroundPattern = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  opacity: 0.15,
  backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
  backgroundSize: '40px 40px',
  pointerEvents: 'none',
  zIndex: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 0%, rgba(34, 211, 238, 0.15), transparent 60%)',
    zIndex: -1,
  }
}));

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
  showHeader?: boolean;
  showDock?: boolean;
  showBackButton?: boolean;
}

const FeedHeaderWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  flexShrink: 0,
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  title = 'SkimBuster',
  showHeader = true,
  showDock = true,
  showBackButton = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const showFeedHeader = location.pathname !== '/settings';

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  return (
    <LayoutContainer>
      <BackgroundPattern />

      {showDock && <SideNavigationBar />}

      <ContentContainer maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {showBackButton && (
          <Box mb={3}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ color: 'text.secondary', '&:hover': { color: '#22d3ee' } }}
            >
              Go Back
            </Button>
          </Box>
        )}

        {/* Feed-style header: page title (when not Feed) + company profile */}
        {showFeedHeader && (
          <FeedHeaderWrapper>
            <Card sx={{ overflow: 'visible' }}>
              <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  {showHeader && title !== 'Feed' && (
                    <Typography
                      component="h1"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 800,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                        letterSpacing: '-0.025em',
                        margin: 0,
                      }}
                    >
                      {title}
                    </Typography>
                  )}
                  <Box sx={{ marginLeft: 'auto' }}>
                    <CompanyProfile compact />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </FeedHeaderWrapper>
        )}

        {children}
      </ContentContainer>
    </LayoutContainer>
  );
};

export default MainLayout;
